import { REQUEST_TIMEOUT_MS } from './endpoints';

/**
 * Thin fetch wrapper -- the ONLY place in the application that calls `fetch`.
 *
 * Centralising transport here means components never build URLs, never set
 * headers, and never parse error shapes. When a Lambda Function URL is
 * deployed, only the environment variable changes.
 */

/** Error type carrying the HTTP status and any server-supplied code. */
export class ApiError extends Error {
  constructor(message, { status = 0, code = 'unknown', details = null } = {}) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

/**
 * Extracts a human-readable message from an unknown error body.
 * Supports the { error: { message } } envelope used by the QAAS Lab
 * Lambda proxy, plus simpler { message } bodies.
 */
const extractErrorMessage = (body, status) => {
  if (body && typeof body === 'object') {
    if (body.error) {
      if (typeof body.error === 'string') return body.error;
      if (body.error.message) return body.error.message;
    }
    if (body.message) return body.message;
  }
  if (status === 429) {
    return 'Too many requests right now. Please wait a moment and try again.';
  }
  if (status >= 500) {
    return 'The service is temporarily unavailable. Please try again shortly.';
  }
  return `The request could not be completed (status ${status}).`;
};

/**
 * Performs a JSON request with a timeout and consistent error handling.
 *
 * @param {string} url - absolute URL to call
 * @param {object} options
 * @param {string} [options.method] - HTTP method, defaults to GET
 * @param {object} [options.body] - JSON-serialisable request body
 * @param {object} [options.headers] - additional headers
 * @param {number} [options.timeoutMs] - override the default timeout
 * @param {AbortSignal} [options.signal] - caller-provided abort signal
 * @returns {Promise<object>} parsed JSON response body
 * @throws {ApiError} on network failure, timeout, or non-2xx response
 */
export async function requestJson(url, options = {}) {
  const {
    method = 'GET',
    body,
    headers = {},
    timeoutMs = REQUEST_TIMEOUT_MS,
    signal,
  } = options;

  if (!url) {
    throw new ApiError('No endpoint is configured for this request.', {
      code: 'not_configured',
    });
  }

  // Combine the caller's signal with our own timeout so either can cancel.
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
  const onExternalAbort = () => controller.abort();

  if (signal) {
    if (signal.aborted) controller.abort();
    else signal.addEventListener('abort', onExternalAbort);
  }

  let response;
  try {
    response = await fetch(url, {
      method,
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        ...headers,
      },
      body: body === undefined ? undefined : JSON.stringify(body),
      signal: controller.signal,
    });
  } catch (error) {
    if (error && error.name === 'AbortError') {
      const timedOut = !(signal && signal.aborted);
      throw new ApiError(
        timedOut
          ? 'The request took too long to respond. Please try again.'
          : 'The request was cancelled.',
        { code: timedOut ? 'timeout' : 'aborted' }
      );
    }
    throw new ApiError(
      'We could not reach the server. Please check your connection and try again.',
      { code: 'network' }
    );
  } finally {
    clearTimeout(timeoutId);
    if (signal) signal.removeEventListener('abort', onExternalAbort);
  }

  // 204 / empty bodies are valid for a fire-and-forget submission.
  const raw = await response.text();
  let payload = {};
  if (raw) {
    try {
      payload = JSON.parse(raw);
    } catch {
      if (!response.ok) {
        throw new ApiError(`The request failed (status ${response.status}).`, {
          status: response.status,
          code: 'invalid_response',
        });
      }
      payload = { message: raw };
    }
  }

  if (!response.ok || (payload && payload.error)) {
    throw new ApiError(extractErrorMessage(payload, response.status), {
      status: response.status,
      code: payload?.error?.code || payload?.code || 'request_failed',
      details: payload?.error || payload,
    });
  }

  return payload;
}

export default requestJson;
